import {FlowModule} from '@fantasmagames/slot/src/modules/flow/FlowModule';
import {Action} from '@fantasmagames/slot/src/logic/Action';
import {AvalancheReelsEvents} from '@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReelsEvents';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {WinPresentationEvents} from '@fantasmagames/slot/src/modules/winpresentation/WinPresentationEvents';
import {FlowWinningState} from '@fantasmagames/slot/src/modules/flow/states/FlowWinningState';
import {BigWinEvents} from '@fantasmagames/slot/src/modules/bigwin/BigWinEvents';

export class FlowAvalancheState extends FlowWinningState {
	public onEnterState(): void {
	}

	public onLeaveState(): void {
		super.onLeaveState();
	}

	protected setupEvents(): void {
		this.on(AvalancheReelsEvents.AVALANCHE_FINISHED, this.onAvalancheEnded);
	}

	protected hasAnyBeforeWinFeatures(): boolean {
		return false;
	}

	protected addBeforeWinFeatures(): void {
	}

	protected onAvalancheEnded(): void {
		this._queueEvents.clearEventsQueue();

		if (this.hasAnyBeforeWinFeatures()) {
			this.addBeforeWinFeatures();
		} else {
			this.decideOnNextAction();
		}
	}

	protected decideOnNextAction(): void {
		switch (this.module.logic.nextAction) {
			case Action.SPIN: {
				this.setupNextStateSpinFlow();
				break;
			}

			case Action.RESPIN: {
				this.setupNextStateRespin();
				break;
			}

			case Action.FREESPIN: {
				this.handleNextActionFreeSpin();
				break;
			}

			case Action.PICK: {
				this.setupNextStateBonusGameFlow();
				break;
			}

			case Action.AVALANCHE: {
				this.setupNextStateAvalancheFlow();
				break;
			}

			default:
				this.setupOtherActions(this.module.logic.nextAction);
				break;
		}
	}

	protected setupOtherActions(nextAction: string): void {

	}

	protected handleNextActionFreeSpin(): void {
		this._queueEvents.addFunctionCallingEventToWaitingList(FlowModuleEvents.FREESPINS_WIN, () => {
			this.module.gotoState(FlowModule.FREE_SPINS_STATE);
		});
		this.setupNextStateFreeSpinsFlow();
	}

	protected setupNextStateRespin(): void {
		this.emitDelayed(FlowModuleEvents.RESPIN_WON);
		this.module.gotoState(FlowModule.RESPIN_STATE);
	}

	protected setupNextStateFreeSpinsFlow(): void {
		if (this.isSpinWin()) {
			if (this.isBigWin()) {
				this._queueEvents.addEventToWaitingList(BigWinEvents.FINISHED, FlowModuleEvents.FREESPINS_WIN);
				this.setupBigWinNoIndividualNoSpin();
				this.setupCantSpinBigWin();
			} else {
				this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.FREESPINS_WIN);
				this.setupCantSpinWin();
			}
		} else {
			this.setupCantSpinNoWin();
			this.emit(FlowModuleEvents.FREESPINS_WIN);
		}
	}

	protected setupNextStateSpinFlow(): void {
		if (this.isBigWin()) {
			this.setupBigWin();
			this.addCheckForExitFreeSpin();
		} else if (this.isSpinWin()) {
			this.setupWin();
			this.addCheckForExitFreeSpin();
		} else {
			this.setupNoWin();
		}
	}

	protected addCheckForExitFreeSpin(): void {
		if (this.module.logic.isFreeSpinsStarted && this.module.logic.freeSpinsLeft <= 0) {
			this._queueEvents.addFunctionCallingEventToWaitingList(
				WinPresentationEvents.INITIAL_ANIMATION_FINISHED,
				this.gotoBasicState.bind(this),
			);
		}
	}

	protected setupBigWin(): void {
		this._queueEvents.addFunctionCallingEventToWaitingList(FlowModuleEvents.CAN_SPIN, () => {
			this.module.gotoState(FlowModule.BASIC_STATE);
		});
		this._queueEvents.addEventToWaitingList(BigWinEvents.FINISHED, FlowModuleEvents.CAN_SPIN);
		this.emit(FlowModuleEvents.WON_BIG_WIN);
	}

	protected setupWin(): void {
		this._queueEvents.addFunctionCallingEventToWaitingList(FlowModuleEvents.CAN_SPIN, () => {
			this.module.gotoState(FlowModule.BASIC_STATE);
		});
		this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_STARTED, FlowModuleEvents.CAN_SPIN);
		this.emit(FlowModuleEvents.WON);
	}

	protected setupNextStateBonusGameFlow(): void {
		this._queueEvents.addFunctionCallingEventToWaitingList(FlowModuleEvents.BONUS_GAME_START, () => {
			this.module.gotoState(FlowModule.BONUS_STATE);
		});

		if (this.isBigWin()) {
			this.setupBigWinNoIndividualNoSpin();
			this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.BONUS_GAME_WON);
			this._queueEvents.addEventToWaitingList(FlowModuleEvents.BONUS_GAME_WON, FlowModuleEvents.BONUS_GAME_START);
		} else if (this.isSpinWin()) {
			this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.BONUS_GAME_WON);
			this._queueEvents.addEventToWaitingList(FlowModuleEvents.BONUS_GAME_WON, FlowModuleEvents.BONUS_GAME_START);
			this.setupCantSpinWin();
		} else {
			this.setupCantSpinNoWin();
			this.emit(FlowModuleEvents.BONUS_GAME_WON);
			this.emit(FlowModuleEvents.BONUS_GAME_START);
		}
	}

	protected setupNextStateAvalancheFlow(): void {
		if (this.isBigWin()) {
			this.setupBigWinNoIndividualNoSpin();
			this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.AVALANCHE_WON);
		} else if (this.isSpinWin()) {
			this.setupCantSpinWin();
			this._queueEvents.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.AVALANCHE_WON);
		} else {
			this.emit(FlowModuleEvents.AVALANCHE_WON);
			this.setupCantSpinNoWin();
		}
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/flow/states/FlowAvalancheState.ts